import { type ReactNode, type ButtonHTMLAttributes } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
  className?: string;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: [
    'bg-accent-brand text-text-inverse',
    'hover:opacity-90',
    'active:opacity-100 active:scale-[0.98]',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'shadow-sm',
  ].join(' '),
  secondary: [
    'bg-surface-elevated text-text-primary',
    'border border-border-strong',
    'hover:bg-surface-subtle hover:border-border-strong',
    'active:bg-surface-base',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'shadow-sm',
  ].join(' '),
  ghost: [
    'bg-transparent text-text-secondary',
    'hover:bg-surface-subtle hover:text-text-primary',
    'active:bg-surface-base',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  ].join(' '),
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-comp',
  md: 'px-4 py-2 text-sm rounded-comp',
  lg: 'px-6 py-3 text-base rounded-comp',
  xl: 'px-7 py-3.5 text-base rounded-card',
};

export function Button({
  variant,
  size = 'md',
  children,
  className = '',
  disabled,
  type = 'button',
  ...props
}: ButtonProps): JSX.Element {
  const baseStyles = [
    'inline-flex items-center justify-center',
    'font-medium',
    'transition-all duration-150',
    'focus:outline-none focus:ring-2 focus:ring-accent-brand/50 focus:ring-offset-2 focus:ring-offset-surface-base',
  ].join(' ');

  return (
    <button
      type={type}
      disabled={disabled}
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
